import { GetStaticProps } from "next";
import Head from "next/head";
import styled from "@emotion/styled";
import { RichSearch } from "goi_common/src/components/common/RichSearch";
import { GOI_WEB_URL, colors } from "goi_common";

/**
 * 무빈소 vs 가족장 GEO 페이지 (서사형 아티클)
 *
 * - 헤이상조 브랜드(다크 + blue 액센트 + Pretendard) GEO 페이지.
 * - canonical 다리: canonical 을 네이버 블로그로 지정해 구글이 네이버 글을 정본 색인하도록 유도.
 *   검증된 조합 og:url=self / canonical=naver. (self 로 "고치지" 말 것 - 의도된 설계)
 * - 섹션 헤더가 전부 질문형이라 FAQPage schema 로 매핑. 실한 본문은 다리의 soft-404 방지용.
 * - CTA 는 goifuneral 견적 퍼널 + UTM(utm_source=heysangjo 표준). 배너 이미지는 에셋 확보 후 교체.
 *
 * ponytail: 브랜드 토큰(t)·Sheet·Section·CTA 가 hwajangjang-yeyak 와 중복.
 *   guide 페이지가 3개째가 되면 src/components 로 공용 primitive 추출할 것.
 */

// 헤이상조 브랜드 토큰 (다크 테마 + blue 액센트)
const t = {
  accent: colors.blue650,
  headerBg: colors.blue900,
  text: colors.white,
  textSecondary: colors.gray300,
  textMuted: colors.gray500,
  surface: colors.gray800,
  border: colors.gray700,
};

const SLUG = "mubinso-gajokjang";
const SELF_URL = `https://heysangjo.co.kr/guide/${SLUG}/`;
// canonical 다리 타겟: 네이버 블로그 원문(모바일 m.blog.naver.com 표기로 통일 - 검증된 패턴)
const NAVER_CANONICAL = "https://m.blog.naver.com/goifuneral/224348760502";
// UTM: utm_source=heysangjo 표준(참고: dogsound-utm-source-standardization)
const CTA_URL = `${GOI_WEB_URL}/match/request/?utm_source=heysangjo&utm_medium=geo_guide&utm_campaign=${SLUG}`;

type Faq = { q: string; a: string };

// 답변은 본문 노출 문장과 동일하게 유지 (구글 FAQ 리치결과 요건)
const FAQ_DATA: Faq[] = [
  {
    q: "가족장과 무빈소는 무엇이 다른가요?",
    a: "갈림길은 '빈소를 차려 조문을 받느냐' 하나입니다. 가족장은 빈소는 차리되 조문을 가족·가까운 지인 위주로만 조용히 받는 소규모 장례이고, 무빈소는 빈소를 아예 차리지 않고 조문 없이 안치→입관→화장으로 모시는 방식입니다.",
  },
  {
    q: "'무빈소 가족장'은 별개의 장례 형태인가요?",
    a: "아닙니다. 흔히 말하는 무빈소 가족장은 제3의 형태가 아니라 대개 '가족끼리 조용히 치르는 무빈소'를 가리키는 표현입니다.",
  },
  {
    q: "무빈소는 비용과 절차가 어떻게 다른가요?",
    a: "빈소 대여료, 조문객 접객 음식, 도우미 비용 등이 줄어 부담이 낮아지고, 3일간의 빈소·조문 대신 안치→입관·염습→발인→화장·봉안으로 절차가 간결해집니다.",
  },
  {
    q: "무빈소는 예우가 소홀한 장례인가요?",
    a: "아닙니다. 빼는 것은 조문·접객의 거품일 뿐, 고인을 모시는 입관·염습·예우는 그대로입니다.",
  },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ_DATA.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export const getStaticProps: GetStaticProps = async () => {
  const title = "무빈소 vs 가족장, 무엇이 다를까 - 무빈소 가족장 뜻·비용·절차 | 헤이상조";
  const description =
    "무빈소와 가족장의 차이, '무빈소 가족장'의 정확한 뜻, 빈소·조문을 뺐을 때 달라지는 비용과 절차, 그래도 그대로 지켜지는 입관·염습 예우까지 - 실제 사례로 정리했습니다.";

  return {
    props: {
      pageDescription: description,
      pageOgTitle: title,
      pageOgDescription: description,
      pageKeywords: "무빈소, 가족장, 무빈소 가족장, 무빈소 장례, 가족장 비용, 무빈소 절차, 소규모 장례",
      pageOgImage: "",
    },
  };
};

const MubinsoGajokjangPage = () => {
  return (
    <>
      <Head>
        {/* canonical → 네이버 원문(다리), og:url → self (검증된 조합) */}
        <link rel="canonical" href={NAVER_CANONICAL} />
        <meta property="og:url" content={SELF_URL} />
        <meta property="og:type" content="article" />
      </Head>
      <RichSearch data={FAQ_SCHEMA} />

      <Sheet>
        <Header>
          <Eyebrow>장례 준비 가이드</Eyebrow>
          <Title>무빈소로 할까, 가족장으로 할까</Title>
          <Lead>
            장례를 준비하다 보면 이 둘을 두고 고민하는 분이 많아요. 그런데 차이는 생각보다 단순하고,
            흔히 말하는 &lsquo;무빈소 가족장&rsquo;이 무엇인지도 알고 나면 어렵지 않아요. 갑작스레
            친정어머니를 떠나보내고 무빈소로 조용히 배웅한 한 딸의 이야기와 함께 정리해 볼게요.
          </Lead>
        </Header>

        <Section>
          <H2>가족장과 무빈소, 무엇이 다를까</H2>
          <P>
            보통 이 둘은 각각 별개의 장례 형태로 운영돼요. 갈림길은 하나,{" "}
            <b>&lsquo;빈소를 차려 조문을 받느냐&rsquo;</b>예요.
          </P>
          <CompareList>
            <li>
              <Term>가족장</Term> 빈소는 차리되, 조문을 가족·가까운 지인 위주로만 조용히 받는 소규모
              장례예요.
            </li>
            <li>
              <Term>무빈소</Term> 빈소를 아예 차리지 않고, 조문 없이 안치 → 입관 → 화장으로 모시는
              방식이에요.
            </li>
          </CompareList>
          <P>
            소규모라도 조문을 받고 싶다면 가족장, 조문 없이 가족끼리 조용히 모시고 싶다면 무빈소인
            거죠.
          </P>
        </Section>

        <Section>
          <H2>&lsquo;무빈소 가족장&rsquo;이라는 말, 왜 헷갈릴까</H2>
          <P>
            무빈소는 빈소가 없다 보니 자연히 가족끼리만 치르게 돼요. 그래서 이걸{" "}
            <b>&lsquo;무빈소 가족장&rsquo;</b>이라 부르는 분이 많죠. 즉 흔히 말하는 무빈소 가족장은
            별개의 제3의 형태가 아니라, 대개 <b>&lsquo;가족끼리 조용히 치르는 무빈소&rsquo;</b>를
            가리키는 표현이에요. &ldquo;번잡함 없이 담백하게 보내드리고 싶다&rdquo;는 가족들이 가장
            많이 택하고요.
          </P>
        </Section>

        <Section>
          <H2>비용과 절차는 어떻게 다를까</H2>
          <P>
            가장 큰 차이는 <b>빈소·조문에 드는 비용과 절차가 빠진다</b>는 점이에요.
          </P>
          <CompareList>
            <li>
              <Term>비용</Term> 빈소 대여료, 조문객 접객 음식, 도우미 비용 등이 줄어 부담이 확
              낮아져요.
            </li>
            <li>
              <Term>절차</Term> 3일간의 빈소·조문 대신, 안치 → 입관·염습 → 발인 → 화장·봉안으로
              간결해지죠.
            </li>
          </CompareList>
          <P>
            다만 오해하기 쉬운 게 하나 있어요. &lsquo;무빈소=대충&rsquo;이 아니라는 것. 빼는 건
            조문·접객의 &lsquo;거품&rsquo;이지, 고인을 모시는 <b>입관·염습·예우는 그대로</b>예요.
          </P>
        </Section>

        <Section>
          <H2>거품은 빼도, 예우는 그대로</H2>
          <P>
            갑작스레 어머니를 떠나보낸 그 딸도 처음엔 막막했지만, 수원연화장장례식장에서 고이
            장례지도사의 조용하고 정중한 안내로 어머니를 무난히 배웅했어요. 과한 것은 덜어냈지만,
            마음만은 조금도 덜어내지 않은 장례였죠.
          </P>
          <P>
            처음엔 빈소가 없어서 어머니께 죄송한 마음이 들었지만, 정중하게 진행되는 입관식을 보며
            슬픔에만 온전히 집중할 수 있었던 시간이었죠.
          </P>
          <Quote>
            &ldquo;무빈소 가족장으로 과소비 등 모든 거품을 뺀 장례식이었지만, 다 끝난 지금 돌아보아도
            만족스럽습니다.&rdquo;
            <cite>- 후기 중에서</cite>
          </Quote>
        </Section>

        <Section>
          <H2>우리 가족에겐 무엇이 맞을까</H2>
          <CompareList>
            <li>
              <Term>가족장</Term> 가족과 가까운 지인 위주로, 50~100명 안팎의 소규모 조문을 받고 싶을
              때
            </li>
            <li>
              <Term>무빈소</Term> 빈소도 조문도 두지 않고, 가족끼리 안치부터 화장까지 조용히 모시고
              싶을 때
            </li>
          </CompareList>
          <P>
            무엇을 택하든 중요한 건 형식이 아니라 &lsquo;어떻게 배웅하느냐&rsquo;예요. 고이는 장례를
            &ldquo;끝맺음이자 새로운 시작&rdquo;이라 말해요. 잘 보내드린 시간은 남은 가족에게 오래
            남으니까요. 그 시작은 의외로 간단해요. 우리 가족에게 맞는 형태와 예상 비용이 어느 정도일지,
            가볍게 가늠해 보는 것부터 시작해볼 수 있어요.
          </P>
        </Section>

        {/* CTA (배너 이미지 확보 시 아래 링크에 이미지 삽입, 링크는 그대로 UTM 유지) */}
        <Section>
          <CtaBox>
            <CtaHeadline>슬픔은 줄일 수 없어도, 막막함은 미리 덜 수 있습니다</CtaHeadline>
            <CtaSub>
              우리 가족 상황(일반 3일장부터 무빈소 가족장까지)에 딱 맞는 예상 비용을 30초 만에 무료로
              확인해 보세요. 불필요한 품목은 덜어내고 꼭 필요한 것만 정직하게.
            </CtaSub>
            <CtaPrimary href={CTA_URL}>30초 무료 예상 비용 확인</CtaPrimary>
            <CtaPhone href="tel:16669784">📞 24시간 긴급 장례 접수·상담 1666-9784</CtaPhone>
          </CtaBox>
        </Section>

        <SourceNote>
          출처: 고이 상조 후기 · 수원연화장장례식장 사례. 고객·고인·가족의 실명과 식별정보는 모두
          익명 처리했습니다.
        </SourceNote>
      </Sheet>
    </>
  );
};

/* ---------------- styles ---------------- */

const Sheet = styled.article`
  color: ${t.text};
  letter-spacing: -0.005em;
`;

const Header = styled.header`
  padding: 20px 20px 18px;
  background: ${t.headerBg};
`;

const Eyebrow = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: ${t.accent};
  letter-spacing: 0.08em;
  margin-bottom: 8px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 800;
  color: ${t.text};
  margin: 0 0 12px;
  letter-spacing: -0.02em;
  line-height: 1.3;
`;

const Lead = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.7;
  color: ${t.textSecondary};
`;

const Section = styled.section`
  padding: 22px 20px 0;
`;

const H2 = styled.h2`
  font-size: 18px;
  font-weight: 800;
  color: ${t.text};
  margin: 0 0 12px;
  letter-spacing: -0.02em;
`;

const P = styled.p`
  margin: 12px 0;
  font-size: 14px;
  line-height: 1.7;
  color: ${t.textSecondary};

  b {
    color: ${t.text};
    font-weight: 700;
  }
`;

const CompareList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 12px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;

  li {
    background: ${t.surface};
    border-radius: 12px;
    padding: 14px 16px;
    font-size: 13.5px;
    line-height: 1.6;
    color: ${t.textSecondary};
  }
`;

const Term = styled.b`
  display: block;
  color: ${t.accent};
  font-weight: 800;
  margin-bottom: 4px;
`;

const Quote = styled.blockquote`
  margin: 16px 0 4px;
  padding: 2px 0 2px 16px;
  border-left: 3px solid ${t.accent};
  font-size: 14.5px;
  line-height: 1.65;
  color: ${t.text};
  font-weight: 500;

  cite {
    display: block;
    margin-top: 8px;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    color: ${t.textMuted};
  }
`;

const CtaBox = styled.div`
  border: 1.5px solid ${t.accent};
  border-radius: 16px;
  padding: 20px;
  text-align: center;
`;

const CtaHeadline = styled.div`
  font-size: 16px;
  font-weight: 800;
  color: ${t.text};
  margin-bottom: 8px;
  letter-spacing: -0.02em;
  line-height: 1.4;
`;

const CtaSub = styled.p`
  margin: 0 0 16px;
  font-size: 13px;
  line-height: 1.6;
  color: ${t.textSecondary};
`;

const CtaPrimary = styled.a`
  display: block;
  background: ${t.accent};
  color: ${t.text};
  text-decoration: none;
  font-weight: 700;
  font-size: 15px;
  padding: 15px;
  border-radius: 12px;
  margin-bottom: 10px;
`;

const CtaPhone = styled.a`
  display: block;
  color: ${t.text};
  text-decoration: none;
  font-weight: 700;
  font-size: 14px;
`;

const SourceNote = styled.div`
  padding: 24px 20px 28px;
  font-size: 10.5px;
  line-height: 1.6;
  color: ${t.textMuted};
`;

export default MubinsoGajokjangPage;
